// ==========================================================
// 100 Stages Catalog & Workpiece Definitions for Paint Meister
// 10 Categories × 10 Stages = 100 Handcrafted Unique Workpieces
// ==========================================================

import type { Language } from '../i18n/translations';
import { furnitureDrawFns } from './shapes/furnitureShapes';
import { vehicleDrawFns } from './shapes/vehicleShapes';
import { musicDrawFns } from './shapes/musicShapes';
import { gourmetDrawFns } from './shapes/gourmetShapes';
import { toyDrawFns } from './shapes/toyShapes';
import { craftDrawFns } from './shapes/craftShapes';
import { scifiDrawFns } from './shapes/scifiShapes';
import { animalDrawFns } from './shapes/animalShapes';
import { fashionDrawFns } from './shapes/fashionShapes';
import { relicDrawFns } from './shapes/relicShapes';
import { landmarkDrawFns } from './shapes/landmarkShapes';

export type StageCategory =
  | 'furniture'
  | 'vehicles'
  | 'music'
  | 'gourmet'
  | 'toys'
  | 'crafts'
  | 'scifi'
  | 'animals'
  | 'fashion'
  | 'relics'
  | 'landmarks';

export interface TargetColorSpec {
  hex: string;
  name: string;
  nameEn: string;
  recipe?: {
    cyan: number;
    magenta: number;
    yellow: number;
    black: number;
    white: number;
  };
}

export interface StageInfo {
  id: number;
  category: StageCategory;
  name: string;
  nameEn: string;
  subtitle: string;
  subtitleEn: string;
  icon: string;
  difficulty: '初級' | '中級' | '上級' | '塗聖級';
  difficultyEn: 'Beginner' | 'Intermediate' | 'Advanced' | 'Grand Meister';
  parTimeSeconds: number;
  unlockCost: number;
  targetColors: TargetColorSpec[];
  description: string;
  descriptionEn: string;
  drawObject: (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    mode: 'base' | 'outline' | 'targetPreview'
  ) => void;
}

export const CATEGORIES: Array<{ id: StageCategory; name: string; nameEn: string; icon: string }> = [
  { id: 'furniture', name: '家具・インテリア', nameEn: 'Furniture', icon: '🪑' },
  { id: 'vehicles', name: '乗り物・マシン', nameEn: 'Vehicles', icon: '🏎️' },
  { id: 'music', name: '楽器・サウンド', nameEn: 'Music & Audio', icon: '🎸' },
  { id: 'gourmet', name: 'スイーツ・フード', nameEn: 'Sweets & Food', icon: '🍦' },
  { id: 'toys', name: 'レトロホビー', nameEn: 'Toys & Retro', icon: '🤖' },
  { id: 'crafts', name: '縁起物・伝統工芸', nameEn: 'Lucky Crafts', icon: '🐱' },
  { id: 'scifi', name: '宇宙・サイエンス', nameEn: 'Sci-Fi & Cosmos', icon: '🚀' },
  { id: 'animals', name: '動物トイ・自然', nameEn: 'Animal Toys', icon: '🐾' },
  { id: 'fashion', name: 'ファッション・ギア', nameEn: 'Street Fashion', icon: '👟' },
  { id: 'relics', name: '塗聖の伝説工芸', nameEn: 'Meister Relics', icon: '👑' },
  { id: 'landmarks', name: '歴史的建造物・名所', nameEn: 'World Landmarks', icon: '🏯' }
];

export function getLocalizedStage(stage: StageInfo, lang: Language) {
  return {
    ...stage,
    name: lang === 'ja' ? stage.name : stage.nameEn,
    subtitle: lang === 'ja' ? stage.subtitle : stage.subtitleEn,
    description: lang === 'ja' ? stage.description : stage.descriptionEn,
    difficulty: lang === 'ja' ? stage.difficulty : stage.difficultyEn,
    targetColors: stage.targetColors.map(tc => ({
      ...tc,
      name: lang === 'ja' ? tc.name : tc.nameEn
    }))
  };
}

// Master Artisan Color Swatches (Refined Adult Craft Palette)
export const PALETTE = {
  blue: '#2574A9',        // Craft Indigo Blue
  deepBlue: '#1F3A93',    // Royal Cobalt
  navy: '#162A45',        // Midnight Aizome
  red: '#C0392B',         // Deep Crimson
  crimson: '#96281B',     // Urushi Vermilion
  ruby: '#7B1113',        // Imperial Ruby
  gold: '#E5A93C',        // Yamabuki Gold Leaf
  yellow: '#E0B034',      // Mustard Ochre
  emerald: '#16A085',     // Celadon Jade Green
  green: '#2E7D32',       // Tokiwa Forest
  forest: '#1B4D3E',      // Rikyu Moss
  cyan: '#1BA39C',        // Celadon Cyan
  turquoise: '#16807B',   // Turquoise Patina
  purple: '#6C4A8D',      // Ancient Imperial Purple
  lavender: '#5E4B8B',    // Wisteria Lavender
  pink: '#D97089',        // Antique Rosewood Pink
  coral: '#E06A55',       // Madder Coral
  orange: '#D35400',      // Persimmon Amber
  amber: '#B33915',       // Antique Amber
  white: '#FDFBF7',       // Gofun Washi White
  ivory: '#EAE4D9',       // Aged Ivory
  dark: '#1E272E',        // Midnight Carbon
  black: '#12171A',       // Jet Black Lacquer
  wood: '#B88B4A',        // Natural Keyaki Wood
  darkWood: '#6E4323',    // Walnut Dark Wood
  brass: '#C59B27',       // Aged Brass
  silver: '#A4B0BE',      // Titanium Satin Silver
  charcoal: '#2F3542'     // Smoked Tile Charcoal
};

// Shape Dispatcher Map aggregating all categories
const allShapeFns: Record<number, (ctx: CanvasRenderingContext2D, cx: number, cy: number, mode: 'base' | 'outline' | 'targetPreview') => void> = {
  ...furnitureDrawFns,
  ...vehicleDrawFns,
  ...musicDrawFns,
  ...gourmetDrawFns,
  ...toyDrawFns,
  ...craftDrawFns,
  ...scifiDrawFns,
  ...animalDrawFns,
  ...fashionDrawFns,
  ...relicDrawFns,
  ...landmarkDrawFns
};

function createStage(
  id: number,
  category: StageCategory,
  name: string,
  nameEn: string,
  subtitle: string,
  subtitleEn: string,
  icon: string,
  difficulty: '初級' | '中級' | '上級' | '塗聖級',
  difficultyEn: 'Beginner' | 'Intermediate' | 'Advanced' | 'Grand Meister',
  parTimeSeconds: number,
  unlockCost: number,
  targetColors: TargetColorSpec[]
): StageInfo {
  const drawFn = allShapeFns[id];
  if (!drawFn) {
    throw new Error(`Critical Error: Missing distinct vector drawing function for Stage ID ${id} (${name})`);
  }

  return {
    id,
    category,
    name,
    nameEn,
    subtitle,
    subtitleEn,
    icon,
    difficulty,
    difficultyEn,
    parTimeSeconds,
    unlockCost,
    targetColors,
    description: `職人技で『${name}』を見本通りに美しく塗装しよう！`,
    descriptionEn: `Apply master painting techniques to finish the ${nameEn}!`,
    drawObject: (ctx, width, height, mode) => {
      const cx = width / 2;
      const cy = height / 2;
      ctx.save();
      drawFn(ctx, cx, cy, mode);
      ctx.restore();
    }
  };
}

export const STAGES: StageInfo[] = [
  // 1. FURNITURE (1 - 10)
  createStage(1, 'furniture', '木製スツール', 'Wooden Stool', '丸椅子をツートンカラーで塗装', 'Two-tone finish on wooden stool', '🪑', '初級', 'Beginner', 90, 0, [
    { hex: PALETTE.blue, name: 'ロイヤルブルー', nameEn: 'Royal Blue' },
    { hex: PALETTE.wood, name: '天然木ナチュラル', nameEn: 'Natural Wood' }
  ]),
  createStage(2, 'furniture', 'レトロ丸型ポスト', 'Vintage Postbox', '昭和レトロな街頭郵便ポスト', 'Classic red pillar street postbox', '📮', '初級', 'Beginner', 100, 0, [
    { hex: PALETTE.crimson, name: 'ポストレッド', nameEn: 'Postbox Crimson' },
    { hex: PALETTE.white, name: '純白ホワイト', nameEn: 'Pristine White' },
    { hex: PALETTE.black, name: '鋳鉄ブラック', nameEn: 'Cast Iron Black' }
  ]),
  createStage(3, 'furniture', '青磁と茜の花瓶', 'Ceramic Vase', '美しいくびれを持つ陶芸花瓶', 'Artisanal porcelain vase gradient', '🏺', '中級', 'Intermediate', 110, 0, [
    { hex: PALETTE.cyan, name: '青磁シアン', nameEn: 'Celadon Cyan' },
    { hex: PALETTE.coral, name: '茜色コーラル', nameEn: 'Madder Coral' },
    { hex: PALETTE.gold, name: '金彩ゴールド', nameEn: 'Gold Glaze' }
  ]),
  createStage(4, 'furniture', 'ダイニングチェア', 'Dining Chair', '北欧モダンの背もたれ椅子', 'Nordic wooden chair with padded seat', '🪑', '初級', 'Beginner', 90, 0, [
    { hex: PALETTE.emerald, name: 'フォレストグリーン', nameEn: 'Forest Green' },
    { hex: PALETTE.wood, name: 'オークウッド', nameEn: 'Oak Wood' }
  ]),
  createStage(5, 'furniture', 'デスクランプ', 'Banker Desk Lamp', 'エメラルドシェードと真鍮スタンド', 'Emerald glass banker lamp with brass', '💡', '上級', 'Advanced', 130, 0, [
    { hex: PALETTE.emerald, name: 'エメラルドグリーン', nameEn: 'Emerald Green' },
    { hex: PALETTE.brass, name: '真鍮ブラスゴールド', nameEn: 'Brass Gold' },
    { hex: PALETTE.dark, name: 'アンティークブラック', nameEn: 'Antique Dark' }
  ]),
  createStage(6, 'furniture', 'レトロ八角掛け時計', 'Wall Clock', '真鍮振り子と木製八角フレーム', 'Octagonal wooden wall clock with pendulum', '🕰️', '中級', 'Intermediate', 100, 150, [
    { hex: PALETTE.darkWood, name: 'ウォールナット', nameEn: 'Walnut Wood' },
    { hex: PALETTE.ivory, name: '文字盤アイボリー', nameEn: 'Dial Ivory' },
    { hex: PALETTE.gold, name: '真鍮振り子ゴールド', nameEn: 'Brass Pendulum' }
  ]),
  createStage(7, 'furniture', '3段オープン本棚', 'Wooden Bookshelf', 'カラフルな背表紙の本が並ぶ本棚', 'Three-tier bookshelf with colorful books', '📚', '中級', 'Intermediate', 110, 200, [
    { hex: PALETTE.darkWood, name: 'ダークウッド', nameEn: 'Dark Wood' },
    { hex: PALETTE.cyan, name: 'ブックシアン', nameEn: 'Book Cyan' },
    { hex: PALETTE.coral, name: 'ブックコーラル', nameEn: 'Book Coral' }
  ]),
  createStage(8, 'furniture', 'ロータス・カフェテーブル', 'Coffee Table', '北欧調の丸型天板と三本脚テーブル', 'Nordic round tabletop with tripod legs', '☕', '中級', 'Intermediate', 110, 200, [
    { hex: PALETTE.ivory, name: '天板アイボリー', nameEn: 'Ivory Tabletop' },
    { hex: PALETTE.wood, name: 'オーク脚', nameEn: 'Oak Legs' },
    { hex: PALETTE.gold, name: 'ゴールドリング', nameEn: 'Gold Ring' }
  ]),
  createStage(9, 'furniture', 'ヴィンテージ置き鏡', 'Vanity Mirror', '真鍮スタンド付きオーバルミラー', 'Oval vanity mirror with brass pedestal', '🪞', '上級', 'Advanced', 120, 250, [
    { hex: PALETTE.gold, name: '金装飾フレーム', nameEn: 'Gold Frame' },
    { hex: PALETTE.cyan, name: 'ミラーガラス', nameEn: 'Mirror Glass' },
    { hex: PALETTE.brass, name: '真鍮スタンド', nameEn: 'Brass Stand' }
  ]),
  createStage(10, 'furniture', '蒔絵アンティーク箪笥', 'Master Cabinet', '黒漆と金彩金具の最高級引き出し箪笥', 'Glossy black lacquer antique drawer cabinet', '🚪', '塗聖級', 'Grand Meister', 140, 300, [
    { hex: PALETTE.black, name: '極黒漆', nameEn: 'Black Lacquer' },
    { hex: PALETTE.crimson, name: '朱赤引き出し', nameEn: 'Vermilion Drawers' },
    { hex: PALETTE.gold, name: '純金引手金具', nameEn: 'Gold Pulls' }
  ]),

  // 2. VEHICLES (11 - 20)
  createStage(11, 'vehicles', 'レーシング・ミニカー', 'Racing Car', 'マスキングでセンターストライプを入れる', 'Racing stripes with masking tape', '🏎️', '中級', 'Intermediate', 120, 100, [
    { hex: PALETTE.deepBlue, name: 'レーシングブルー', nameEn: 'Racing Blue' },
    { hex: PALETTE.white, name: 'ストライプホワイト', nameEn: 'Stripe White' },
    { hex: PALETTE.black, name: 'タイヤブラック', nameEn: 'Tire Black' }
  ]),
  createStage(12, 'vehicles', '塗聖のサーフボード', 'Meister Surfboard', '3色トリプルグラデーション仕上げ', '3-color ocean gradient on surfboard', '🏄', '中級', 'Intermediate', 120, 150, [
    { hex: PALETTE.cyan, name: 'オーシャンシアン', nameEn: 'Ocean Cyan' },
    { hex: PALETTE.gold, name: 'サンセットイエロー', nameEn: 'Sunset Yellow' },
    { hex: PALETTE.coral, name: 'コーラルピンク', nameEn: 'Coral Pink' }
  ]),
  createStage(13, 'vehicles', 'カフェレーサー・バイク', 'Cafe Racer Bike', 'ティアドロップタンクとスポークホイール', 'Teardrop tank and spoked wheels', '🏍️', '上級', 'Advanced', 130, 200, [
    { hex: PALETTE.crimson, name: 'タンククリムゾン', nameEn: 'Tank Crimson' },
    { hex: PALETTE.silver, name: 'エンジンシルバー', nameEn: 'Engine Silver' },
    { hex: PALETTE.black, name: 'タイヤブラック', nameEn: 'Tire Black' }
  ]),
  createStage(14, 'vehicles', 'レトロスクールバス', 'Vintage Bus', 'レトロな黄色いボディとブラックライン', 'Classic yellow school bus with stripe', '🚌', '初級', 'Beginner', 100, 200, [
    { hex: PALETTE.yellow, name: 'スクールイエロー', nameEn: 'School Yellow' },
    { hex: PALETTE.black, name: 'ラインブラック', nameEn: 'Stripe Black' },
    { hex: PALETTE.cyan, name: 'ウィンドウシアン', nameEn: 'Window Cyan' }
  ]),
  createStage(15, 'vehicles', 'クラシック蒸気機関車', 'Steam Locomotive', '煙突とボイラーバンドの重厚な機関車', 'Heavy locomotive with chimney and brass bands', '🚂', '上級', 'Advanced', 130, 250, [
    { hex: PALETTE.black, name: 'ボイラーブラック', nameEn: 'Boiler Black' },
    { hex: PALETTE.crimson, name: 'ランニングボード赤', nameEn: 'Red Running Board' },
    { hex: PALETTE.brass, name: '真鍮ドームゴールド', nameEn: 'Brass Steam Dome' }
  ]),
  createStage(16, 'vehicles', 'プロペラ複葉機', 'Biplane Airplane', '上下2枚翼と回転プロペラのヴィンテージ機', 'Classic biplane with twin wings and propeller', '🛩️', '中級', 'Intermediate', 120, 250, [
    { hex: PALETTE.deepBlue, name: 'スカイブルー胴体', nameEn: 'Sky Blue Fuselage' },
    { hex: PALETTE.gold, name: '主翼ゴールド', nameEn: 'Gold Wings' },
    { hex: PALETTE.silver, name: 'プロペラ銀', nameEn: 'Propeller Silver' }
  ]),
  createStage(17, 'vehicles', 'レスキュー・ヘリコプター', 'Rescue Helicopter', '回転ローターとバブル風防の救助ヘリ', 'Rescue chopper with cockpit and top rotor', '🚁', '中級', 'Intermediate', 120, 300, [
    { hex: PALETTE.orange, name: 'ハイビズオレンジ', nameEn: 'Hi-Vis Orange' },
    { hex: PALETTE.white, name: 'コックピット白', nameEn: 'Cockpit White' },
    { hex: PALETTE.dark, name: 'ローターダーク', nameEn: 'Rotor Dark' }
  ]),
  createStage(18, 'vehicles', '黄色い深海潜水艦', 'Yellow Submarine', '丸窓ポロポロと潜望鏡の深海探査艇', 'Yellow exploration sub with portholes', '🚤', '上級', 'Advanced', 130, 300, [
    { hex: PALETTE.yellow, name: 'サブマリンイエロー', nameEn: 'Sub Yellow' },
    { hex: PALETTE.cyan, name: '丸窓シアン', nameEn: 'Porthole Cyan' },
    { hex: PALETTE.crimson, name: 'キールストライプ', nameEn: 'Keel Stripe' }
  ]),
  createStage(19, 'vehicles', '冒険の気球ゴンドラ', 'Hot Air Balloon', '縞模様の気球と籐編みゴンドラ', 'Ribbed hot air balloon and basket', '🎈', '中級', 'Intermediate', 120, 350, [
    { hex: PALETTE.coral, name: 'コーラルピンク', nameEn: 'Coral Pink' },
    { hex: PALETTE.gold, name: 'サンシャインイエロー', nameEn: 'Sunshine Yellow' },
    { hex: PALETTE.cyan, name: 'スカイシアン', nameEn: 'Sky Cyan' }
  ]),
  createStage(20, 'vehicles', 'グランド・ヨット', 'Grand Yacht', '白波を蹴立てるスマートな外洋ヨット', 'Ocean racing yacht with white sails', '⛵', '塗聖級', 'Grand Meister', 140, 400, [
    { hex: PALETTE.navy, name: 'ディープネイビー船体', nameEn: 'Deep Navy Hull' },
    { hex: PALETTE.white, name: '純白メインセイル', nameEn: 'White Main Sail' },
    { hex: PALETTE.gold, name: 'ゴールドウォーターライン', nameEn: 'Gold Waterline' }
  ]),

  // 3. MUSIC (21 - 30)
  createStage(21, 'music', 'ロック・エレキギター', 'Rock Guitar', 'サンバースト塗装とピックガード', 'Sunburst gradient on electric guitar', '🎸', '中級', 'Intermediate', 120, 150, [
    { hex: PALETTE.gold, name: 'アンバーゴールド', nameEn: 'Amber Gold' },
    { hex: PALETTE.crimson, name: 'チェリーサンバースト', nameEn: 'Cherry Sunburst' },
    { hex: PALETTE.white, name: 'ピックガードホワイト', nameEn: 'Pickguard White' }
  ]),
  createStage(22, 'music', 'レトロ木製ラジオ', 'Vintage Radio', '丸型スピーカーメッシュとチューニングダイヤル', 'Vintage tabletop radio with speaker mesh', '📻', '中級', 'Intermediate', 110, 150, [
    { hex: PALETTE.cyan, name: 'ミントシアン', nameEn: 'Mint Cyan' },
    { hex: PALETTE.ivory, name: 'スピーカーアイボリー', nameEn: 'Speaker Ivory' },
    { hex: PALETTE.gold, name: '真鍮ダイヤル', nameEn: 'Brass Dial' }
  ]),
  createStage(23, 'music', 'アコースティックギター', 'Acoustic Guitar', 'くびれのある響板とロゼッタサウンドホール', 'Dreadnought acoustic guitar with rosette', '🪕', '中級', 'Intermediate', 110, 200, [
    { hex: PALETTE.wood, name: 'スプルーストップ', nameEn: 'Spruce Top' },
    { hex: PALETTE.darkWood, name: 'ローズウッドネック', nameEn: 'Rosewood Neck' },
    { hex: PALETTE.black, name: 'サウンドホール黒', nameEn: 'Black Soundhole' }
  ]),
  createStage(24, 'music', 'ストラディバリ・バイオリン', 'Stradivari Violin', '優美なF字孔と飴色ニス仕上げのバイオリン', 'Stradivari violin with glossy amber varnish', '🎻', '上級', 'Advanced', 130, 250, [
    { hex: PALETTE.amber, name: '琥珀ニスアンバー', nameEn: 'Amber Varnish' },
    { hex: PALETTE.gold, name: '黄金木目ハイライト', nameEn: 'Gold Grain' },
    { hex: PALETTE.black, name: '黒檀指板', nameEn: 'Ebony Fingerboard' }
  ]),
  createStage(25, 'music', '黄金のサックス', 'Golden Saxophone', 'J字型チューブとキィボタンが輝くサックス', 'Golden alto saxophone with polished bell', '🎷', '上級', 'Advanced', 130, 250, [
    { hex: PALETTE.gold, name: 'ブラスゴールド管体', nameEn: 'Brass Gold' },
    { hex: PALETTE.brass, name: 'キィカップ真鍮', nameEn: 'Key Cups' },
    { hex: PALETTE.white, name: '白蝶貝ボタン', nameEn: 'Pearl Buttons' }
  ]),
  createStage(26, 'music', 'ロックンロール・ドラム', 'Rock Snare Drum', 'クロームリムとテンションロッドのスネア', 'Snare drum with chrome rims and rods', '🥁', '中級', 'Intermediate', 110, 250, [
    { hex: PALETTE.crimson, name: 'スパークルレッド胴', nameEn: 'Sparkle Red Shell' },
    { hex: PALETTE.white, name: '打面ヘッド白', nameEn: 'White Drumhead' },
    { hex: PALETTE.silver, name: 'クロームフープ銀', nameEn: 'Chrome Hoops' }
  ]),
  createStage(27, 'music', 'グランドピアノ', 'Grand Piano', '優美な翼型ボディと白黒鍵盤の王様', 'Grand piano with curved rim and keys', '🎹', '上級', 'Advanced', 140, 300, [
    { hex: PALETTE.black, name: '漆黒ピアノブラック', nameEn: 'Piano Black' },
    { hex: PALETTE.white, name: '象牙鍵盤ホワイト', nameEn: 'Ivory Keys' },
    { hex: PALETTE.gold, name: '金文字ロゴ', nameEn: 'Gold Inlay' }
  ]),
  createStage(28, 'music', 'アナログ・ターンテーブル', 'Vinyl Turntable', '回転するLPレコードとトーンアーム針', 'Vinyl record turntable and tonearm', '🎛️', '中級', 'Intermediate', 120, 300, [
    { hex: PALETTE.dark, name: 'チャコールキャビネット', nameEn: 'Dark Cabinet' },
    { hex: PALETTE.black, name: 'LPレコード盤', nameEn: 'Vinyl Record' },
    { hex: PALETTE.crimson, name: 'センターラベル赤', nameEn: 'Center Label' }
  ]),
  createStage(29, 'music', '真鍮のトランペット', 'Brass Trumpet', '3連ピストンバルブと広がりのあるベル', 'Brass trumpet with three piston valves', '🎺', '中級', 'Intermediate', 120, 350, [
    { hex: PALETTE.gold, name: 'ゴールドブラス', nameEn: 'Gold Brass' },
    { hex: PALETTE.brass, name: 'バルブブロック', nameEn: 'Valve Block' },
    { hex: PALETTE.silver, name: 'マウスピース銀', nameEn: 'Silver Mouthpiece' }
  ]),
  createStage(30, 'music', 'ビンテージ・スタジオマイク', 'Studio Microphone', '金属メッシュグリルとショックマウント', 'Vintage ribbon studio microphone', '🎙️', '塗聖級', 'Grand Meister', 140, 400, [
    { hex: PALETTE.silver, name: 'サテンクローム', nameEn: 'Satin Chrome' },
    { hex: PALETTE.dark, name: 'スタンドブラック', nameEn: 'Stand Black' },
    { hex: PALETTE.gold, name: 'ダイヤフラム金彩', nameEn: 'Gold Diaphragm' }
  ]),

  // 4. GOURMET (31 - 40)
  createStage(31, 'gourmet', 'トロピカル・ソフトクリーム', 'Soft Cream Toy', 'ストロベリー＆マンゴーの渦巻きアイス', 'Strawberry & mango swirl cone', '🍦', '初級', 'Beginner', 90, 100, [
    { hex: PALETTE.pink, name: 'ベリーピンク', nameEn: 'Berry Pink' },
    { hex: PALETTE.yellow, name: 'マンゴーイエロー', nameEn: 'Mango Yellow' },
    { hex: PALETTE.orange, name: 'コーンブラウン', nameEn: 'Waffle Brown' }
  ]),
  createStage(32, 'gourmet', '苺のショートケーキ', 'Strawberry Cake', 'ふんわり生クリームと真っ赤な大粒苺', 'Fluffy sponge cake with fresh strawberry', '🍰', '初級', 'Beginner', 100, 150, [
    { hex: PALETTE.white, name: '純白ホイップ', nameEn: 'Pure Whipped Cream' },
    { hex: PALETTE.gold, name: 'スポンジケーキ黄色', nameEn: 'Sponge Yellow' },
    { hex: PALETTE.crimson, name: '完熟ストロベリー', nameEn: 'Ripe Strawberry' }
  ]),
  createStage(33, 'gourmet', 'カラフル・ドーナツ', 'Glazed Donut', 'ピンクアイシングとスプリンクルのリング', 'Pink glazed donut with colorful sprinkles', '🍩', '初級', 'Beginner', 90, 150, [
    { hex: PALETTE.wood, name: '揚げ生地ブラウン', nameEn: 'Donut Dough' },
    { hex: PALETTE.pink, name: 'ベリーアイシング', nameEn: 'Berry Glaze' },
    { hex: PALETTE.cyan, name: 'トッピングスプリンクル', nameEn: 'Sprinkles' }
  ]),
  createStage(34, 'gourmet', '贅沢フルーツパフェ', 'Fruit Parfait', 'グラスに詰まったアイスクリームとシロップ', 'Tall parfait glass with ice cream scoops', '🍨', '中級', 'Intermediate', 110, 200, [
    { hex: PALETTE.coral, name: 'ベリーアイス', nameEn: 'Berry Ice' },
    { hex: PALETTE.cyan, name: 'ミントアイス', nameEn: 'Mint Ice' },
    { hex: PALETTE.white, name: 'バニラクリーム', nameEn: 'Vanilla Cream' }
  ]),
  createStage(35, 'gourmet', 'クラシック・バーガー', 'Gourmet Burger', 'ジューシーパティ・とろけるチーズ・レタス', 'Juicy gourmet burger with cheese and lettuce', '🍔', '中級', 'Intermediate', 110, 200, [
    { hex: PALETTE.orange, name: 'セサミバンズ', nameEn: 'Sesame Buns' },
    { hex: PALETTE.darkWood, name: 'グリルパティ', nameEn: 'Grilled Patty' },
    { hex: PALETTE.yellow, name: 'チェダーチーズ', nameEn: 'Cheddar Cheese' }
  ]),
  createStage(36, 'gourmet', '熱々イタリアンピザ', 'Italian Pizza', 'ペパロニとモッツァレラのとろけるスライス', 'Pizza slice with melted mozzarella & pepperoni', '🍕', '中級', 'Intermediate', 110, 250, [
    { hex: PALETTE.yellow, name: 'モッツァレラチーズ', nameEn: 'Mozzarella Cheese' },
    { hex: PALETTE.crimson, name: 'ペパロニサラミ', nameEn: 'Pepperoni Salami' },
    { hex: PALETTE.emerald, name: 'バジルリーフ', nameEn: 'Fresh Basil' }
  ]),
  createStage(37, 'gourmet', 'レインボー・ロリポップ', 'Rainbow Lollipop', '同心円状の渦巻きキャンディスティック', 'Swirled colorful lollipop candy on stick', '🍭', '中級', 'Intermediate', 100, 250, [
    { hex: PALETTE.pink, name: 'チェリーピンク', nameEn: 'Cherry Pink' },
    { hex: PALETTE.cyan, name: 'ソーダシアン', nameEn: 'Soda Cyan' },
    { hex: PALETTE.yellow, name: 'レモンイエロー', nameEn: 'Lemon Yellow' }
  ]),
  createStage(38, 'gourmet', 'パステル・マカロン', 'Macaron Tower', '3段重ねのふっくらパステルマカロン', 'Three-tier pastel macarons with cream filling', '🧁', '上級', 'Advanced', 120, 300, [
    { hex: PALETTE.lavender, name: 'ラベンダーパープル', nameEn: 'Lavender Purple' },
    { hex: PALETTE.pink, name: 'ローズピンク', nameEn: 'Rose Pink' },
    { hex: PALETTE.cyan, name: 'ピスタチオシアン', nameEn: 'Pistachio Cyan' }
  ]),
  createStage(39, 'gourmet', '原宿チョコバナナクレープ', 'Sweet Crepe', '三角包みのクレープにチョコソース', 'Folded crepe with chocolate sauce and whip', '🥞', '中級', 'Intermediate', 110, 300, [
    { hex: PALETTE.gold, name: 'クレープ生地ゴールド', nameEn: 'Crepe Dough' },
    { hex: PALETTE.white, name: 'ホイップクリーム', nameEn: 'Whipped Cream' },
    { hex: PALETTE.darkWood, name: 'ビターチョコソース', nameEn: 'Chocolate Sauce' }
  ]),
  createStage(40, 'gourmet', '職人の木製寿司盛り合わせ', 'Artisanal Sushi Toy', '木製下駄盛りのマグロ・サーモン・玉子', 'Artisanal wooden sushi tray with tuna & salmon', '🍣', '塗聖級', 'Grand Meister', 140, 400, [
    { hex: PALETTE.wood, name: '白木下駄ゲタ', nameEn: 'Wooden Tray' },
    { hex: PALETTE.crimson, name: '本マグロ赤身', nameEn: 'Tuna Red' },
    { hex: PALETTE.coral, name: '極上サーモン', nameEn: 'Salmon Coral' }
  ]),

  // 5. TOYS (41 - 50)
  createStage(41, 'toys', 'レトロトイ・ロボット', 'Toy Robot', 'ブリキ製ゼンマイロボット', 'Classic tin robot with metallic armor', '🤖', '上級', 'Advanced', 140, 150, [
    { hex: PALETTE.dark, name: 'メカニカルグレー', nameEn: 'Mechanical Gray' },
    { hex: PALETTE.red, name: 'シグナルレッド', nameEn: 'Signal Red' },
    { hex: PALETTE.yellow, name: 'アイイエロー', nameEn: 'Eye Yellow' }
  ]),
  createStage(42, 'toys', 'ビンテージ・カメラ', 'Vintage Camera', '丸型レンズ鏡胴とシルバーボディのカメラ', 'Classic rangefinder camera with lens barrel', '📷', '中級', 'Intermediate', 110, 150, [
    { hex: PALETTE.silver, name: 'サテンシルバー', nameEn: 'Satin Silver' },
    { hex: PALETTE.dark, name: 'レザーグリップ黒', nameEn: 'Leather Grip' },
    { hex: PALETTE.cyan, name: 'コーティングレンズ青', nameEn: 'Coated Lens' }
  ]),
  createStage(43, 'toys', 'RCドリフトカー', 'RC Drift Car', 'ワイドタイヤと大型リアウイングのバギー', 'Off-road RC buggy with spoiler and big tires', '🏎️', '中級', 'Intermediate', 120, 200, [
    { hex: PALETTE.cyan, name: 'ネオンシアンボディ', nameEn: 'Neon Cyan Body' },
    { hex: PALETTE.purple, name: 'キャノピーパープル', nameEn: 'Canopy Purple' },
    { hex: PALETTE.yellow, name: 'ウイングイエロー', nameEn: 'Wing Yellow' }
  ]),
  createStage(44, 'toys', 'レトロアーケード筐体', 'Arcade Cabinet', '上部マーキーとCRT画面のゲーム筐体', 'Classic arcade machine with screen & joystick', '🕹️', '上級', 'Advanced', 130, 250, [
    { hex: PALETTE.dark, name: '筐体ブラック', nameEn: 'Cabinet Dark' },
    { hex: PALETTE.crimson, name: '電飾マーキー赤', nameEn: 'Marquee Red' },
    { hex: PALETTE.cyan, name: 'CRT画面グロー青', nameEn: 'CRT Screen' }
  ]),
  createStage(45, 'toys', 'ドールハウス', 'Dollhouse', '三角屋根と出窓のあるミニチュアハウス', 'Miniature dollhouse with gabled roof & door', '🏠', '上級', 'Advanced', 130, 250, [
    { hex: PALETTE.ivory, name: '外壁アイボリー', nameEn: 'Ivory Wall' },
    { hex: PALETTE.pink, name: '屋根瓦ピンク', nameEn: 'Pink Roof' },
    { hex: PALETTE.emerald, name: '玄関ドアグリーン', nameEn: 'Green Door' }
  ]),
  createStage(46, 'toys', '真鍮の天体望遠鏡', 'Brass Telescope', '三脚スタンドと真鍮光学鏡筒の望遠鏡', 'Brass refracting telescope on wooden tripod', '🔭', '中級', 'Intermediate', 120, 300, [
    { hex: PALETTE.brass, name: '真鍮鏡筒ゴールド', nameEn: 'Brass Tube' },
    { hex: PALETTE.gold, name: '対物フード金', nameEn: 'Objective Hood' },
    { hex: PALETTE.darkWood, name: '三脚マホガニー', nameEn: 'Mahogany Tripod' }
  ]),
  createStage(47, 'toys', 'パズルキューブ', 'Puzzle Cube', '立体3Dパズルの多面カラーリング', 'Isometric 3D puzzle cube with facet tiles', '🎲', '中級', 'Intermediate', 110, 300, [
    { hex: PALETTE.yellow, name: '天面イエロー', nameEn: 'Top Yellow' },
    { hex: PALETTE.red, name: '左面レッド', nameEn: 'Left Red' },
    { hex: PALETTE.deepBlue, name: '右面ブルー', nameEn: 'Right Blue' }
  ]),
  createStage(48, 'toys', 'ゼンマイ仕掛けの車', 'Wind-up Car', '屋根に大きな金色のゼンマイ鍵を持つミニカー', 'Cartoon coupe with golden winding key', '🚗', '中級', 'Intermediate', 120, 350, [
    { hex: PALETTE.crimson, name: 'ボディクリムゾン', nameEn: 'Crimson Body' },
    { hex: PALETTE.gold, name: 'ゼンマイ鍵ゴールド', nameEn: 'Gold Winding Key' },
    { hex: PALETTE.black, name: 'タイヤブラック', nameEn: 'Tire Black' }
  ]),
  createStage(49, 'toys', '木製プロヨーヨー', 'Wooden Yo-Yo', '同心円の木製ディスクとゴールドコア', 'Precision wooden yo-yo with gold axle hub', '🪀', '初級', 'Beginner', 90, 350, [
    { hex: PALETTE.crimson, name: 'リムクリムゾン', nameEn: 'Crimson Rim' },
    { hex: PALETTE.wood, name: '銘木ディスク', nameEn: 'Wood Disk' },
    { hex: PALETTE.gold, name: '軸芯ゴールド', nameEn: 'Gold Core' }
  ]),
  createStage(50, 'toys', 'ガチャガチャマシン', 'Gacha Machine', '透明球体ドームとコインレバーの販売機', 'Retro capsule vending machine with globe', '🎰', '塗聖級', 'Grand Meister', 140, 450, [
    { hex: PALETTE.crimson, name: '本体レッド', nameEn: 'Machine Red' },
    { hex: PALETTE.cyan, name: 'カプセルドーム青', nameEn: 'Capsule Dome' },
    { hex: PALETTE.white, name: 'レバーホワイト', nameEn: 'Lever White' }
  ]),

  // 6. CRAFTS (51 - 60)
  createStage(51, 'crafts', '福招き猫', 'Lucky Maneki-Neko', '白磁の体に朱赤首輪と小判ゴールド', 'Porcelain white coat with gold coin', '🐱', '中級', 'Intermediate', 120, 150, [
    { hex: PALETTE.white, name: '胡粉ホワイト', nameEn: 'Porcelain White' },
    { hex: PALETTE.crimson, name: '朱赤漆', nameEn: 'Vermilion Lacquer' },
    { hex: PALETTE.gold, name: '本金小判ゴールド', nameEn: 'Pure Coin Gold' }
  ]),
  createStage(52, 'crafts', '必勝祈願・赤だるま', 'Victory Daruma', '金色の髭と白い顔の丸いだるま', 'Traditional round red victory daruma', '👺', '初級', 'Beginner', 100, 150, [
    { hex: PALETTE.crimson, name: '必勝ダルマ朱赤', nameEn: 'Daruma Red' },
    { hex: PALETTE.white, name: '白面フェイス', nameEn: 'White Face' },
    { hex: PALETTE.gold, name: '金文字蒔絵', nameEn: 'Gold Whiskers' }
  ]),
  createStage(53, 'crafts', '京扇子・桜金箔', 'Kyo Sensu Fan', '放射状に広がる美しい金箔と桜の扇子', 'Radiating traditional folding fan with gold leaf', '🪭', '上級', 'Advanced', 130, 200, [
    { hex: PALETTE.pink, name: '桜色ピンク地', nameEn: 'Cherry Pink' },
    { hex: PALETTE.gold, name: '純金箔装飾', nameEn: 'Pure Gold Leaf' },
    { hex: PALETTE.darkWood, name: '竹骨要ブラウン', nameEn: 'Bamboo Ribs' }
  ]),
  createStage(54, 'crafts', '鳴子こけし人形', 'Kokeshi Doll', '丸い頭とおかっぱ前髪の伝統木工こけし', 'Hand-carved wooden kokeshi doll with bangs', '🎎', '初級', 'Beginner', 90, 200, [
    { hex: PALETTE.ivory, name: '木肌アイボリー', nameEn: 'Wood Ivory' },
    { hex: PALETTE.black, name: 'おかっぱ黒髪', nameEn: 'Black Hair' },
    { hex: PALETTE.crimson, name: '着物草花模様', nameEn: 'Kimono Red' }
  ]),
  createStage(55, 'crafts', '江戸風鈴・金魚', 'Glass Wind Chime', 'ガラスボウルと揺れる短冊の江戸風鈴', 'Glass bell wind chime with swimming goldfish', '🎐', '中級', 'Intermediate', 110, 250, [
    { hex: PALETTE.cyan, name: '硝子シアン', nameEn: 'Glass Cyan' },
    { hex: PALETTE.coral, name: '蒔絵金魚朱色', nameEn: 'Goldfish Coral' },
    { hex: PALETTE.white, name: '和紙短冊', nameEn: 'Washi Streamer' }
  ]),
  createStage(56, 'crafts', '備前焼・信楽壺', 'Artisan Pottery Urn', '素朴な自然釉と耳付きの陶芸大壺', 'Rustic ceramic urn with ash glaze drip', '🏺', '上級', 'Advanced', 130, 250, [
    { hex: PALETTE.darkWood, name: '備前焼土肌', nameEn: 'Unglazed Clay' },
    { hex: PALETTE.wood, name: '窯変ウッド', nameEn: 'Kiln Glow' },
    { hex: PALETTE.emerald, name: '自然灰釉エメラルド', nameEn: 'Ash Glaze' }
  ]),
  createStage(57, 'crafts', '金継ぎの抹茶茶碗', 'Kintsugi Tea Bowl', '割れ目を純金の稲妻模様で繋いだ茶碗', 'Matcha bowl repaired with pure gold veins', '🍵', '上級', 'Advanced', 130, 300, [
    { hex: PALETTE.black, name: '黒楽焼ボディ', nameEn: 'Black Raku' },
    { hex: PALETTE.forest, name: '抹茶グリーン釉', nameEn: 'Matcha Green' },
    { hex: PALETTE.gold, name: '本金継ぎ脈', nameEn: 'Pure Kintsugi Gold' }
  ]),
  createStage(58, 'crafts', '輪島塗・蒔絵文箱', 'Urushi Lacquer Box', '漆黒の光沢と桜蒔絵が優美な硯箱', 'Glossy black lacquer stationery box with maki-e', '📦', '上級', 'Advanced', 140, 350, [
    { hex: PALETTE.black, name: '本漆黒塗り', nameEn: 'Black Lacquer' },
    { hex: PALETTE.crimson, name: '内面朱漆', nameEn: 'Inner Vermilion' },
    { hex: PALETTE.gold, name: '蒔絵桜ゴールド', nameEn: 'Gold Maki-e' }
  ]),
  createStage(59, 'crafts', '幸運の折り鶴', 'Origami Crane Toy', '幾何学的な折り目と大きく広げた翼の鶴', 'Geometric folded paper origami crane', '🕊️', '中級', 'Intermediate', 110, 350, [
    { hex: PALETTE.gold, name: '千代紙ゴールド', nameEn: 'Chiyogami Gold' },
    { hex: PALETTE.coral, name: '左翼コーラル', nameEn: 'Left Wing Coral' },
    { hex: PALETTE.purple, name: '右翼パープル', nameEn: 'Right Wing Purple' }
  ]),
  createStage(60, 'crafts', '魔除けの鬼瓦', 'Onigawara Demon Tile', '大棟を飾る一対の角と牙を持つ鬼瓦', 'Roof ridge demon tile with curved horns and fangs', '👹', '塗聖級', 'Grand Meister', 150, 450, [
    { hex: PALETTE.charcoal, name: '燻し瓦チャコール', nameEn: 'Smoked Tile' },
    { hex: PALETTE.silver, name: '鬼角シルバー', nameEn: 'Demon Horns' },
    { hex: PALETTE.gold, name: '金眼ゴールド', nameEn: 'Golden Eyes' }
  ]),

  // 7. SCI-FI (61 - 70)
  createStage(61, 'scifi', '宇宙探査ロケット', 'Space Rocket', 'ノーズコーンとブースターの未来塗装', 'Futuristic booster fins and nose cone', '🚀', '上級', 'Advanced', 130, 150, [
    { hex: PALETTE.white, name: 'シャトルホワイト', nameEn: 'Shuttle White' },
    { hex: PALETTE.crimson, name: 'ロケットレッド', nameEn: 'Rocket Crimson' },
    { hex: PALETTE.cyan, name: 'エナジーシアン', nameEn: 'Energy Cyan' }
  ]),
  createStage(62, 'scifi', 'スペースシャトル', 'Space Shuttle', '耐熱黒ノーズとデルタ翼の宇宙往還機', 'Delta-winged space orbiter with heat shield', '🛰️', '上級', 'Advanced', 130, 200, [
    { hex: PALETTE.white, name: 'オービターホワイト', nameEn: 'Orbiter White' },
    { hex: PALETTE.dark, name: '耐熱ブラック', nameEn: 'Thermal Black' },
    { hex: PALETTE.cyan, name: 'キャノピーシアン', nameEn: 'Canopy Cyan' }
  ]),
  createStage(63, 'scifi', '宇宙ステーション', 'Space Station', '巨大な太陽電池パドルと居住モジュール', 'Orbital space station with solar array wings', '🛸', '上級', 'Advanced', 130, 250, [
    { hex: PALETTE.deepBlue, name: 'ソーラーパネル青', nameEn: 'Solar Array Blue' },
    { hex: PALETTE.gold, name: '金箔トラスフレーム', nameEn: 'Gold Truss' },
    { hex: PALETTE.silver, name: 'モジュール銀', nameEn: 'Module Silver' }
  ]),
  createStage(64, 'scifi', '真鍮の天球儀', 'Orrery Planetarium', '太陽系惑星の同心軌道環が回転する儀', 'Concentric brass orbital rings and sun sphere', '🪐', '塗聖級', 'Grand Meister', 140, 300, [
    { hex: PALETTE.brass, name: '真鍮軌道リング', nameEn: 'Brass Orbit Rings' },
    { hex: PALETTE.purple, name: '深宇宙パープル', nameEn: 'Deep Space Purple' },
    { hex: PALETTE.gold, name: '中心太陽ゴールド', nameEn: 'Center Sun Gold' }
  ]),
  createStage(65, 'scifi', 'UFOフライングソーサー', 'Alien Flying Saucer', '透明キャノピードームと発光ランプの円盤', 'Classic flying saucer with glowing lamps', '🛸', '中級', 'Intermediate', 110, 250, [
    { hex: PALETTE.silver, name: '円盤シルバー', nameEn: 'Saucer Silver' },
    { hex: PALETTE.purple, name: 'ドームパープル', nameEn: 'Dome Purple' },
    { hex: PALETTE.emerald, name: 'ビームエメラルド', nameEn: 'Beam Emerald' }
  ]),
  createStage(66, 'scifi', '火星探査ローバー', 'Mars Rover', '6輪ロッカーボギーとパノラマカメラマスト', 'Six-wheeled Mars rover with camera mast', '🚜', '上級', 'Advanced', 130, 300, [
    { hex: PALETTE.gold, name: '耐熱ゴールドフォイル', nameEn: 'Gold Thermal Foil' },
    { hex: PALETTE.charcoal, name: 'シャーシチャコール', nameEn: 'Chassis Charcoal' },
    { hex: PALETTE.cyan, name: 'センサースコープ青', nameEn: 'Sensor Scope' }
  ]),
  createStage(67, 'scifi', 'プラズマブラスター', 'Plasma Blaster', 'リブ付き発光プラズマシリンダーの光線銃', 'Sci-fi energy handgun with plasma cylinder', '🔫', '中級', 'Intermediate', 120, 350, [
    { hex: PALETTE.dark, name: 'ガンメタルダーク', nameEn: 'Gunmetal Dark' },
    { hex: PALETTE.cyan, name: 'プラズマコアシアン', nameEn: 'Plasma Core' },
    { hex: PALETTE.purple, name: 'マズルパープル', nameEn: 'Muzzle Purple' }
  ]),
  createStage(68, 'scifi', 'サイバーヘルメット', 'Cyber Helmet', 'フルフェイスアーマーと湾曲ネオンバイザー', 'Cybernetic armored helmet with curved visor', '🪖', '上級', 'Advanced', 130, 350, [
    { hex: PALETTE.purple, name: 'シェルパープル', nameEn: 'Shell Purple' },
    { hex: PALETTE.cyan, name: 'ネオンバイザー青', nameEn: 'Neon Visor' },
    { hex: PALETTE.yellow, name: 'ダクトイエロー', nameEn: 'Duct Yellow' }
  ]),
  createStage(69, 'scifi', 'アンドロイド', 'Android Assistant', '人工皮膚と回路パターンが光る頭部胸像', 'Synthetic android bust with glowing circuits', '🦾', '上級', 'Advanced', 140, 400, [
    { hex: PALETTE.white, name: 'シンセティック白', nameEn: 'Synthetic White' },
    { hex: PALETTE.cyan, name: 'オプティカルアイ青', nameEn: 'Optical Eye' },
    { hex: PALETTE.gold, name: '回路パターン金', nameEn: 'Circuit Gold' }
  ]),
  createStage(70, 'scifi', 'タイムマシン', 'Time Machine Relic', '回転する時間環と量子砂時計のコア', 'Chrono gyroscopic rings and glowing core', '⏳', '塗聖級', 'Grand Meister', 150, 500, [
    { hex: PALETTE.brass, name: 'クロノ真鍮リング', nameEn: 'Chrono Brass' },
    { hex: PALETTE.purple, name: '時空パープル', nameEn: 'Spacetime Purple' },
    { hex: PALETTE.cyan, name: '量子コアシアン', nameEn: 'Quantum Core' }
  ]),

  // 8. ANIMALS (71 - 80)
  createStage(71, 'animals', 'テディベア', 'Teddy Bear', '温かみあるブラウンとリボン', 'Warm honey brown plush texture', '🧸', '初級', 'Beginner', 90, 150, [
    { hex: PALETTE.wood, name: 'ハニーブラウン', nameEn: 'Honey Brown' },
    { hex: PALETTE.crimson, name: 'リボンレッド', nameEn: 'Ribbon Red' },
    { hex: PALETTE.black, name: 'ボタンブラック', nameEn: 'Button Black' }
  ]),
  createStage(72, 'animals', 'ラバーダック', 'Rubber Duckie', '上向きの嘴と丸いお尻のお風呂アヒル', 'Bubbly rubber duck with orange beak', '🐥', '初級', 'Beginner', 90, 150, [
    { hex: PALETTE.yellow, name: 'アヒルイエロー', nameEn: 'Duck Yellow' },
    { hex: PALETTE.orange, name: '嘴オレンジ', nameEn: 'Beak Orange' },
    { hex: PALETTE.black, name: 'つぶらな瞳黒', nameEn: 'Bead Eyes' }
  ]),
  createStage(73, 'animals', '木彫りフクロウ', 'Carved Owl', '大きな同心円の目と胸羽のフクロウ', 'Hand-carved owl with huge concentric eyes', '🦉', '中級', 'Intermediate', 110, 200, [
    { hex: PALETTE.darkWood, name: 'フクロウ原木', nameEn: 'Owl Wood' },
    { hex: PALETTE.gold, name: '黄金眼ゴールド', nameEn: 'Gold Eyes' },
    { hex: PALETTE.ivory, name: '胸羽アイボリー', nameEn: 'Breast Feathers' }
  ]),
  createStage(74, 'animals', '木製キリン', 'Wooden Giraffe', '長い首と茶色い斑点模様のキリン', 'Slender wooden giraffe with spot patterns', '🦒', '中級', 'Intermediate', 110, 200, [
    { hex: PALETTE.yellow, name: 'キリンイエロー', nameEn: 'Giraffe Yellow' },
    { hex: PALETTE.darkWood, name: 'スポットブラウン', nameEn: 'Spot Brown' },
    { hex: PALETTE.coral, name: '角コーラル', nameEn: 'Horns Coral' }
  ]),
  createStage(75, 'animals', 'ジャイアントパンダ', 'Giant Panda', '特徴的な黒いアイパッチと丸い耳のパンダ', 'Giant panda with iconic black eye patches', '🐼', '初級', 'Beginner', 100, 250, [
    { hex: PALETTE.white, name: 'パンダホワイト', nameEn: 'Panda White' },
    { hex: PALETTE.black, name: 'アイパッチブラック', nameEn: 'Eye Patch Black' },
    { hex: PALETTE.pink, name: '鼻ピンク', nameEn: 'Nose Pink' }
  ]),
  createStage(76, 'animals', 'シロナガスクジラ', 'Blue Whale Toy', '流線形の巨大な体と白い畝腹のクジラ', 'Streamlined blue whale with pleated white belly', '🐋', '中級', 'Intermediate', 110, 250, [
    { hex: PALETTE.deepBlue, name: 'クジラブルー', nameEn: 'Whale Blue' },
    { hex: PALETTE.white, name: '腹部ホワイト', nameEn: 'Belly White' },
    { hex: PALETTE.cyan, name: '潮吹きシアン', nameEn: 'Spout Cyan' }
  ]),
  createStage(77, 'animals', 'モルフォ蝶レリーフ', 'Morpho Butterfly', '左右対称に広がる美しい翅脈の青い蝶', 'Morpho butterfly with radiating wing veins', '🦋', '上級', 'Advanced', 130, 300, [
    { hex: PALETTE.cyan, name: 'モルフォシアン', nameEn: 'Morpho Cyan' },
    { hex: PALETTE.purple, name: '構造色パープル', nameEn: 'Structural Purple' },
    { hex: PALETTE.black, name: '翅縁ブラック', nameEn: 'Wing Rim Black' }
  ]),
  createStage(78, 'animals', 'ティラノサウルス', 'T-Rex Figurine', '強靭な頭骨と鋭い牙の恐竜フィギュア', 'T-Rex skull figurine with jagged teeth', '🦖', '上級', 'Advanced', 130, 350, [
    { hex: PALETTE.forest, name: 'ダイナソーフォレスト', nameEn: 'Dino Forest' },
    { hex: PALETTE.gold, name: '眼窩ゴールド', nameEn: 'Eye Socket Gold' },
    { hex: PALETTE.crimson, name: '鋭牙クリムゾン', nameEn: 'Teeth Crimson' }
  ]),
  createStage(79, 'animals', '皇帝ペンギン', 'Emperor Penguin', 'タキシードのような背中と胸の黄色い毛', 'Emperor penguin with tuxedo back and gold bib', '🐧', '中級', 'Intermediate', 110, 350, [
    { hex: PALETTE.black, name: 'タキシードブラック', nameEn: 'Tuxedo Black' },
    { hex: PALETTE.white, name: '純白腹部', nameEn: 'White Chest' },
    { hex: PALETTE.gold, name: '胸元イエローゴールド', nameEn: 'Gold Ear Patch' }
  ]),
  createStage(80, 'animals', 'アマガエルトイ', 'Lucky Tree Frog', '飛び出た大きな目と吸盤指のカエル', 'Tree frog toy with bulbous eyes and smile', '🐸', '中級', 'Intermediate', 110, 400, [
    { hex: PALETTE.emerald, name: 'アマガエルグリーン', nameEn: 'Tree Frog Green' },
    { hex: PALETTE.yellow, name: '咽頭イエロー', nameEn: 'Throat Yellow' },
    { hex: PALETTE.crimson, name: 'アイリング赤', nameEn: 'Eye Ring Red' }
  ]),

  // 9. FASHION (81 - 90)
  createStage(81, 'fashion', 'ハイテクスニーカー', 'Street Sneaker', 'ソール・アッパー・スウッシュのマルチカラー', 'Custom multi-panel streetwear sneaker', '👟', '上級', 'Advanced', 130, 200, [
    { hex: PALETTE.purple, name: 'ネオンパープル', nameEn: 'Neon Purple' },
    { hex: PALETTE.orange, name: 'ビビッドオレンジ', nameEn: 'Vivid Orange' },
    { hex: PALETTE.white, name: 'ソールホワイト', nameEn: 'Sole White' }
  ]),
  createStage(82, 'fashion', 'スナップバック・キャップ', 'Snapback Cap', '6枚クラウンと平らなバイザーのキャップ', 'Snapback cap with flat bill visor and patch', '🧢', '初級', 'Beginner', 100, 200, [
    { hex: PALETTE.dark, name: 'クラウンブラック', nameEn: 'Crown Dark' },
    { hex: PALETTE.crimson, name: 'フラットバイザー赤', nameEn: 'Flat Brim Red' },
    { hex: PALETTE.white, name: 'フロントパッチ白', nameEn: 'Front Patch' }
  ]),
  createStage(83, 'fashion', 'スケートボードデッキ', 'Skateboard Deck', '中央ストライプとキックテールのデッキ', 'Popsicle skateboard deck with grip stripe', '🛹', '中級', 'Intermediate', 120, 250, [
    { hex: PALETTE.cyan, name: 'デッキシアン', nameEn: 'Deck Cyan' },
    { hex: PALETTE.purple, name: 'センターパープル', nameEn: 'Center Purple' },
    { hex: PALETTE.yellow, name: 'サークルロゴ黄', nameEn: 'Circle Logo' }
  ]),
  createStage(84, 'fashion', 'スクエアサングラス', 'Retro Sunglasses', '太いフレームとグラデーションレンズ', 'Wayfarer sunglasses with gradient lenses', '🕶️', '初級', 'Beginner', 90, 250, [
    { hex: PALETTE.darkWood, name: 'ベッコウフレーム', nameEn: 'Tortoise Frame' },
    { hex: PALETTE.coral, name: 'サンセットレンズ', nameEn: 'Sunset Lens' },
    { hex: PALETTE.gold, name: 'ヒンジゴールド', nameEn: 'Hinge Gold' }
  ]),
  createStage(85, 'fashion', 'ストリートバックパック', 'Street Backpack', '前ポケットとブタ鼻パッチのデイパック', 'Street daypack with front pouch and lash tab', '🎒', '中級', 'Intermediate', 120, 300, [
    { hex: PALETTE.forest, name: 'フォレストボディ', nameEn: 'Forest Body' },
    { hex: PALETTE.orange, name: 'フロントポケット橙', nameEn: 'Front Pouch' },
    { hex: PALETTE.black, name: 'レザーパッチ黒', nameEn: 'Leather Patch' }
  ]),
  createStage(86, 'fashion', 'パイロット・クロノグラフ', 'Pilot Chronograph', '2連プッシャーと3連インダイアルの腕時計', 'Pilot chronograph watch with dual pushers', '⌚', '上級', 'Advanced', 130, 350, [
    { hex: PALETTE.navy, name: 'レザーストラップ紺', nameEn: 'Navy Strap' },
    { hex: PALETTE.silver, name: 'ステンレスケース銀', nameEn: 'Steel Case' },
    { hex: PALETTE.gold, name: 'インダイアル金', nameEn: 'Gold Sub-dials' }
  ]),
  createStage(87, 'fashion', 'DJヘッドホン', 'DJ Headphones', '円形イヤーパッドとヘッドバンド', 'DJ monitor headphones with swivel earcups', '🎧', '中級', 'Intermediate', 120, 350, [
    { hex: PALETTE.black, name: 'ヘッドバンド黒', nameEn: 'Headband Black' },
    { hex: PALETTE.cyan, name: 'ハウジングシアン', nameEn: 'Housing Cyan' },
    { hex: PALETTE.silver, name: 'スイベルシルバー', nameEn: 'Swivel Silver' }
  ]),
  createStage(88, 'fashion', '革ジャン・ライダース', 'Leather Jacket', '斜めジッパーと大きな襟のライダース', 'Biker leather jacket with asymmetrical zipper', '🧥', '上級', 'Advanced', 140, 400, [
    { hex: PALETTE.black, name: 'カウハイドブラック', nameEn: 'Cowhide Black' },
    { hex: PALETTE.crimson, name: 'ラペルクリムゾン', nameEn: 'Lapel Crimson' },
    { hex: PALETTE.silver, name: 'メタルジッパー銀', nameEn: 'Metal Zipper' }
  ]),
  createStage(89, 'fashion', 'ワークブーツ', 'Work Boots', 'ラグソールと鳩目フックの本格ブーツ', 'Rugged work boots with lugged outsole', '🥾', '上級', 'Advanced', 130, 400, [
    { hex: PALETTE.wood, name: 'ヌバックレザー', nameEn: 'Nubuck Leather' },
    { hex: PALETTE.black, name: 'ビブラムソール黒', nameEn: 'Vibram Outsole' },
    { hex: PALETTE.brass, name: 'ハトメ真鍮', nameEn: 'Brass Eyelets' }
  ]),
  createStage(90, 'fashion', 'グラフィティ・スプレー缶', 'Spray Paint Can', 'ノズルキャップとブランド帯のスプレー缶', 'Graffiti aerosol spray paint can with cap', '🎨', '初級', 'Beginner', 90, 450, [
    { hex: PALETTE.cyan, name: '缶ボディシアン', nameEn: 'Can Cyan' },
    { hex: PALETTE.pink, name: 'キャップピンク', nameEn: 'Cap Pink' },
    { hex: PALETTE.silver, name: 'ノズルバルブ銀', nameEn: 'Nozzle Silver' }
  ]),

  // 10. RELICS (91 - 100)
  createStage(91, 'relics', '塗聖の白磁ティーポット', 'Master Tea Pot', 'ロイヤルコバルトと純金金彩', 'Pristine porcelain with royal cobalt & gold', '🫖', '塗聖級', 'Grand Meister', 150, 300, [
    { hex: PALETTE.deepBlue, name: 'ロイヤルコバルト', nameEn: 'Royal Cobalt' },
    { hex: PALETTE.gold, name: '純金金彩ゴールド', nameEn: 'Pure Leaf Gold' },
    { hex: PALETTE.white, name: '白磁ホワイト', nameEn: 'Porcelain White' }
  ]),
  createStage(92, 'relics', '帝国皇帝の黄金クラウン', 'Imperial Crown', 'フルール・ド・リス頂点と宝飾の冠', 'Imperial crown with fleur-de-lis and jewels', '👑', '塗聖級', 'Grand Meister', 150, 400, [
    { hex: PALETTE.gold, name: '純金クラウン', nameEn: 'Pure Gold Crown' },
    { hex: PALETTE.crimson, name: '真紅ベルベット', nameEn: 'Crimson Velvet' },
    { hex: PALETTE.deepBlue, name: 'サファイア宝飾', nameEn: 'Sapphire Jewel' }
  ]),
  createStage(93, 'relics', '大航海時代の財宝箱', 'Pirate Treasure Chest', 'ドーム型蓋と鉄帯バンドの海賊宝箱', 'Arched pirate treasure chest with iron bands', '💎', '塗聖級', 'Grand Meister', 140, 450, [
    { hex: PALETTE.darkWood, name: 'マホガニー宝箱', nameEn: 'Mahogany Chest' },
    { hex: PALETTE.charcoal, name: '鍛鉄バンド黒', nameEn: 'Iron Bands' },
    { hex: PALETTE.gold, name: '黄金錠前ゴールド', nameEn: 'Gold Lock Hasp' }
  ]),
  createStage(94, 'relics', 'クリスタルドラゴン', 'Crystal Dragon', '宝珠を咥えた水晶竜の頭部レリーフ', 'Crystal dragon head holding radiant pearl', '🐉', '塗聖級', 'Grand Meister', 160, 500, [
    { hex: PALETTE.cyan, name: '水晶シアン', nameEn: 'Crystal Cyan' },
    { hex: PALETTE.purple, name: '霊気パープル', nameEn: 'Aura Purple' },
    { hex: PALETTE.gold, name: '神龍宝珠ゴールド', nameEn: 'Dragon Pearl' }
  ]),
  createStage(95, 'relics', '伝説の聖剣エクスカリバー', 'Excalibur Sword', '刀身の血溝と黄金の鍔を持つ伝説の剣', 'Excalibur sword with crossguard and runic fuller', '⚔️', '塗聖級', 'Grand Meister', 150, 500, [
    { hex: PALETTE.silver, name: 'ミスリル銀刀身', nameEn: 'Mithril Blade' },
    { hex: PALETTE.gold, name: '黄金鍔・柄頭', nameEn: 'Gold Crossguard' },
    { hex: PALETTE.cyan, name: 'ルーン光シアン', nameEn: 'Runic Glow' }
  ]),
  createStage(96, 'relics', '不死鳥フェニックスの盾', 'Phoenix Aegis Shield', '翼を広げた不死鳥レリーフのヒーターシールド', 'Heater shield embossed with phoenix wings', '🛡️', '塗聖級', 'Grand Meister', 150, 550, [
    { hex: PALETTE.crimson, name: '緋色シールド地', nameEn: 'Crimson Field' },
    { hex: PALETTE.gold, name: '不死鳥フェニックス金', nameEn: 'Gold Phoenix' },
    { hex: PALETTE.orange, name: '火炎オーラ橙', nameEn: 'Flame Aura' }
  ]),
  createStage(97, 'relics', '千夜一夜の魔法ランプ', 'Genie Magic Lamp', '細長い注ぎ口とアラベスク文様のランプ', 'Arabian magic lamp with genie vapor', '🪔', '塗聖級', 'Grand Meister', 140, 550, [
    { hex: PALETTE.gold, name: 'アンティークゴールド', nameEn: 'Antique Gold' },
    { hex: PALETTE.cyan, name: '魔神の煙シアン', nameEn: 'Genie Smoke' },
    { hex: PALETTE.purple, name: '魔法オーラ紫', nameEn: 'Magic Purple' }
  ]),
  createStage(98, 'relics', '天文オルゴール', 'Astronomy Music Box', 'ガラスドーム内で歯車が連動するオルゴール', 'Clockwork music box with planetary gears', '🎼', '塗聖級', 'Grand Meister', 150, 600, [
    { hex: PALETTE.darkWood, name: '黒檀台座', nameEn: 'Ebony Base' },
    { hex: PALETTE.navy, name: '夜空ドーム紺', nameEn: 'Night Sky Dome' },
    { hex: PALETTE.gold, name: '真鍮歯車ゴールド', nameEn: 'Brass Gears' }
  ]),
  createStage(99, 'relics', '大航海者の天球儀', 'Celestial Globe', '黄道十二宮バンドと地球儀が回転する大天球儀', 'Celestial armillary sphere with zodiac band', '🌐', '塗聖級', 'Grand Meister', 160, 650, [
    { hex: PALETTE.brass, name: '子午線真鍮環', nameEn: 'Meridian Brass' },
    { hex: PALETTE.navy, name: '大洋ネイビー', nameEn: 'Ocean Navy' },
    { hex: PALETTE.gold, name: '黄道帯ゴールド', nameEn: 'Zodiac Band Gold' }
  ]),
  createStage(100, 'relics', '塗聖の極印・最終秘宝', 'Seal of Grand Meister', '極・塗師道の頂点に君臨する八角マイスター印章', 'Grand octagonal master seal of ultimate painting', '✨', '塗聖級', 'Grand Meister', 180, 1000, [
    { hex: PALETTE.gold, name: '至高純金フレーム', nameEn: 'Supreme Gold' },
    { hex: PALETTE.crimson, name: '朱印クリムゾン', nameEn: 'Vermilion Seal' },
    { hex: PALETTE.cyan, name: 'マイスター光彩', nameEn: 'Meister Radiance' }
  ]),

  // 11. LANDMARKS (101 - 112)
  createStage(101, 'landmarks', '富士山と五重塔', 'Mount Fuji & Pagoda', '雪化粧の霊峰富士と朱塗り五重塔の絶景', 'Majestic snow-capped Mt. Fuji with five-story pagoda', '🗻', '中級', 'Intermediate', 130, 200, [
    { hex: PALETTE.deepBlue, name: '群青富士ブルー', nameEn: 'Indigo Fuji Blue' },
    { hex: PALETTE.crimson, name: '五重塔漆朱赤', nameEn: 'Pagoda Vermilion' },
    { hex: PALETTE.white, name: '雪化粧ホワイト', nameEn: 'Snow White' }
  ]),
  createStage(102, 'landmarks', '浅草寺 雷門', 'Kaminarimon Gate', '浅草の象徴たる大提灯と重厚な入母屋瓦屋根', 'Iconic red lantern and historic temple gate of Asakusa', '🏮', '初級', 'Beginner', 110, 150, [
    { hex: PALETTE.red, name: '浅草大提灯深紅', nameEn: 'Asakusa Lantern Red' },
    { hex: PALETTE.crimson, name: '丹塗り柱朱色', nameEn: 'Vermilion Pillars' },
    { hex: PALETTE.charcoal, name: '瓦屋根藍墨', nameEn: 'Roof Tile Charcoal' }
  ]),
  createStage(103, 'landmarks', '姫路城・白鷺城', 'Himeji Castle', '白漆喰の優美な大天守閣と重厚な石垣', 'Pristine white heron castle with fortress stone base', '🏯', '上級', 'Advanced', 150, 400, [
    { hex: PALETTE.white, name: '白漆喰ホワイト', nameEn: 'White Plaster' },
    { hex: PALETTE.silver, name: '石垣鼠グレー', nameEn: 'Stone Wall Gray' },
    { hex: PALETTE.charcoal, name: '天守瓦屋根黒', nameEn: 'Castle Tile Dark' }
  ]),
  createStage(104, 'landmarks', '東京タワー', 'Tokyo Tower', 'インターナショナルオレンジが映える昭和の電波塔', 'Classic retro-modern red and white lattice tower', '🗼', '中級', 'Intermediate', 120, 250, [
    { hex: PALETTE.orange, name: 'インターナショナル橙', nameEn: 'Tower Orange' },
    { hex: PALETTE.white, name: '展望デッキ白', nameEn: 'Observation White' },
    { hex: PALETTE.navy, name: '夜景ネイビー', nameEn: 'Midnight Navy' }
  ]),
  createStage(105, 'landmarks', '厳島神社 水上大鳥居', 'Miyajima Floating Torii', '瀬戸内の青波にそびえ立つ神秘の朱塗り木造鳥居', 'Sacred vermilion torii gate floating on Seto Inland Sea', '⛩️', '初級', 'Beginner', 100, 180, [
    { hex: PALETTE.crimson, name: '宮島丹塗り朱', nameEn: 'Miyajima Vermilion' },
    { hex: PALETTE.blue, name: '瀬戸内海ブルー', nameEn: 'Seto Sea Blue' },
    { hex: PALETTE.gold, name: '扁額黄金ゴールド', nameEn: 'Shrine Plaque Gold' }
  ]),
  createStage(106, 'landmarks', '金閣寺・鹿苑寺', 'Kinkaku-ji Golden Pavilion', '鏡湖池に美しく映える三層の純金箔楼閣', 'Glittering golden temple mirrored on peaceful pond', '✨', '塗聖級', 'Grand Meister', 160, 500, [
    { hex: PALETTE.gold, name: '純金箔ゴールド', nameEn: 'Pure Leaf Gold' },
    { hex: PALETTE.darkWood, name: 'こけら葺き檜皮', nameEn: 'Cypress Bark Roof' },
    { hex: PALETTE.turquoise, name: '鏡湖池エメラルド', nameEn: 'Mirror Pond Emerald' }
  ]),
  createStage(107, 'landmarks', 'エッフェル塔', 'Eiffel Tower', 'パリの空にそびえる錬鉄の美しいレース構造', 'Iron Lady lattice tower rising over the Parisian sky', '🗼', '中級', 'Intermediate', 130, 300, [
    { hex: PALETTE.brass, name: 'パリジャンブロンズ', nameEn: 'Parisian Bronze' },
    { hex: PALETTE.gold, name: 'イルミネーション金', nameEn: 'Illumination Gold' },
    { hex: PALETTE.silver, name: '尖塔シルバー', nameEn: 'Spire Silver' }
  ]),
  createStage(108, 'landmarks', 'ギザの大ピラミッド', 'Pyramids of Giza & Sphinx', '砂漠の陽光を浴びる古代エジプトの巨石遺跡', 'Ancient monumental stone pyramids and guardian Sphinx', '🐫', '初級', 'Beginner', 110, 200, [
    { hex: PALETTE.gold, name: '砂漠ゴールドサンド', nameEn: 'Desert Gold Sand' },
    { hex: PALETTE.orange, name: '夕陽アンバー影', nameEn: 'Sunset Amber Shadow' },
    { hex: PALETTE.amber, name: 'スフィンクス黄土', nameEn: 'Sphinx Ochre' }
  ]),
  createStage(109, 'landmarks', '自由の女神像', 'Statue of Liberty', '緑青の優雅な衣と黄金の松明を掲げる自由の象徴', 'Iconic colossus with verdigris patina holding liberty torch', '🗽', '中級', 'Intermediate', 130, 350, [
    { hex: PALETTE.cyan, name: '緑青ヴェルディグリ', nameEn: 'Verdigris Patina' },
    { hex: PALETTE.gold, name: '自由の松明ゴールド', nameEn: 'Torch Flame Gold' },
    { hex: PALETTE.silver, name: '石造り台座グレー', nameEn: 'Pedestal Stone' }
  ]),
  createStage(110, 'landmarks', 'タージ・マハル', 'Taj Mahal', '白大理石のドームが輝く世界屈指の美しい対称宮殿', 'Pristine white marble mausoleum with symmetry', '🕌', '上級', 'Advanced', 150, 450, [
    { hex: PALETTE.white, name: '純白大理石ホワイト', nameEn: 'Pure Marble White' },
    { hex: PALETTE.ivory, name: 'ミナレットアイボリー', nameEn: 'Minaret Ivory' },
    { hex: PALETTE.navy, name: 'アーチ影ネイビー', nameEn: 'Arch Shadow Navy' }
  ]),
  createStage(111, 'landmarks', 'ピサの斜塔', 'Leaning Tower of Pisa', '絶妙な傾きを保つ美しい8層の大理石ロマネスク鐘楼', 'Famous leaning freestanding bell tower with marble arches', '🏛️', '中級', 'Intermediate', 120, 300, [
    { hex: PALETTE.white, name: '大理石ホワイト', nameEn: 'Tuscan Marble' },
    { hex: PALETTE.ivory, name: 'アーチ回廊アイボリー', nameEn: 'Arcade Ivory' },
    { hex: PALETTE.green, name: '奇跡の広場オリーブ', nameEn: 'Field Lawn Green' }
  ]),
  createStage(112, 'landmarks', 'サグラダ・ファミリア', 'Sagrada Familia', '天に向かってそびえるガウディの未完の超絶大聖堂', 'Gaudis monumental basilica with sculpted spires', '⛪', '塗聖級', 'Grand Meister', 170, 600, [
    { hex: PALETTE.wood, name: 'モンジュイック砂岩', nameEn: 'Montjuic Sandstone' },
    { hex: PALETTE.darkWood, name: '彫刻ファサード茶', nameEn: 'Facade Amber' },
    { hex: PALETTE.gold, name: '頂上モザイク金', nameEn: 'Pinnacle Mosaic Gold' }
  ])
];
